import React, { useState, useEffect } from 'react';
import dpdpConsentConfig from './ConsentBannerconfig';


export default function DpdpConsentInterface({
  onConfirm,
  onClose,
  initial = {}
}) {
  const [config, setConfig] = useState(dpdpConsentConfig.getConfig());
  const [showModal, setShowModal] = useState(true);
  const [showPreferences, setShowPreferences] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [checked, setChecked] = useState({ notice: false, consent: false });

  const buildDefaults = (cfg) => {
    const defaults = {};
    (cfg.categories || []).forEach(cat => {
      defaults[cat.id] = initial[cat.id] ?? cat.defaultOn ?? false;
    });
    return defaults;
  };

  const [preferences, setPreferences] = useState(() => buildDefaults(config));

  // Subscribe to config changes
  useEffect(() => {
    const unsubscribe = dpdpConsentConfig.subscribe((newConfig) => {
      setConfig(newConfig);
    });
    return unsubscribe;
  }, []);

  // Listen for global open event
  useEffect(() => {
    const handleOpen = () => {
      setShowModal(true);
      setShowPreferences(false);
    };

    window.addEventListener('open-dpdp-preferences', handleOpen);
    window.addEventListener('open-dpdp-consent', handleOpen);

    return () => {
      window.removeEventListener('open-dpdp-preferences', handleOpen);
      window.removeEventListener('open-dpdp-consent', handleOpen);
    };
  }, []);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleToggle = (key) => {
    setPreferences(prev => {
      const updated = { ...prev, [key]: !prev[key] };
      return updated;
    });
  };

  const handleClose = () => {
    setShowModal(false);
    if (onClose) onClose();
  };

  const handleConfirm = (prefs) => {
    const full = { necessary: true, ...prefs };
    setShowModal(false);
    if (onConfirm) onConfirm(full);

    // Notify widget of changes (same event name pattern as the cookie widget)
    window.dispatchEvent(new CustomEvent('dpdp-config-updated', { detail: full }));
  };

  if (!showModal) return null;

  const styles = dpdpConsentConfig.getWidgetStyles();
  const primaryBg = config.primaryColor || '#2563eb';

  const canProceed = !config.requireCheckbox || (checked.notice && checked.consent);

  // Dynamic per-tenant values flow in as CSS custom properties; everything
  // else (layout, spacing, responsive rules) lives in the .dpdp-modal-* classes.
  const rootVars = {
    '--dpdp-primary': primaryBg,
    '--dpdp-bg': config.backgroundColor,
    '--dpdp-radius': styles.container.borderRadius,
    '--dpdp-shadow': styles.container.boxShadow,
    '--dpdp-btn-radius': styles.button.borderRadius,
  };

  return (
    <div className="dpdp-modal-overlay" style={rootVars}>
      <div className="dpdp-modal-card">
        {/* Header */}
        <div className="dpdp-modal-header">
          <h2 className="dpdp-modal-title">
            {showPreferences ? 'Manage Consent Preferences' : 'Data Privacy Preference Center'}
          </h2>
          <button className="dpdp-widget-close" onClick={handleClose}>×</button>
        </div>

        {/* Content */}
        <div className="dpdp-modal-body">
          {!showPreferences ? (
            <div>
              <h3 className="dpdp-modal-section-title">{config.title}</h3>
              <p style={{ color: '#4b5563', lineHeight: '1.6', margin: 0 }}>
                {config.subtitle}
              </p>

              {/* Required purposes — informational only */}
              <div style={{ marginTop: '1.25rem' }}>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
                  Purpose of Processing
                </h4>
                <div className="dpdp-modal-purpose-list">
                  {(config.purposes || []).map(p => (
                    <div key={p.id} className="dpdp-modal-purpose-row">
                      <div style={{ paddingRight: 12 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>{p.label}</div>
                        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>{p.description}</div>
                      </div>
                      <span className="dpdp-required-badge">Required</span>
                    </div>
                  ))}
                </div>
              </div>

              {config.privacyUrl ? (
                <a
                  href={config.privacyUrl}
                  style={{ color: primaryBg, display: 'inline-block', marginTop: '1rem', textDecoration: 'none' }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Statement
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() => { }}
                  style={{ background: 'transparent', border: 'none', color: primaryBg, padding: 0, marginTop: '1rem', cursor: 'pointer' }}
                >
                  Privacy Statement
                </button>
              )}

              <div className="dpdp-modal-actions">
                <button
                  className="dpdp-modal-btn dpdp-modal-btn-outline"
                  onClick={() => {
                    const declined = {};
                    (config.categories || []).forEach(c => { declined[c.id] = false; });
                    setPreferences(declined);
                    handleConfirm(declined);
                  }}
                >
                  Decline All
                </button>
                <button
                  className="dpdp-modal-btn dpdp-modal-btn-primary"
                  onClick={() => {
                    const all = {};
                    (config.categories || []).forEach(c => { all[c.id] = true; });
                    setPreferences(all);
                    handleConfirm(all);
                  }}
                >
                  Accept All
                </button>
                <button
                  className="dpdp-modal-btn dpdp-modal-btn-secondary"
                  onClick={() => setShowPreferences(true)}
                >
                  Preferences
                </button>
              </div>
            </div>
          ) : (
            <div>
              <button
                className="dpdp-modal-btn dpdp-modal-btn-primary"
                style={{ marginBottom: '1rem', width: '100%' }}
                onClick={() => {
                  const all = {};
                  (config.categories || []).forEach(c => { all[c.id] = true; });
                  setPreferences(all);
                  handleConfirm(all);
                }}
              >
                Allow All
              </button>

              <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: '1rem' }}>
                Consent Preferences
              </h3>

              {/* Required purposes — read-only */}
              <div style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
                {(config.purposes || []).map(p => (
                  <div key={p.id} className="dpdp-modal-category-card">
                    <div className="dpdp-modal-category-head">
                      <span style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>{p.label}</span>
                      <span className="dpdp-required-badge">Required</span>
                    </div>
                    <div style={{ padding: 12 }}>
                      <p style={{ color: '#374151', margin: 0 }}>{p.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Optional categories — toggleable */}
              <div style={{ display: 'grid', gap: 12 }}>
                {(config.categories || []).map(category => (
                  <div key={category.id} className="dpdp-modal-category-card">
                    <div className="dpdp-modal-category-head">
                      <button
                        onClick={() => toggleSection(category.id)}
                        style={{
                          background: 'transparent', border: 'none', textAlign: 'left', flex: 1,
                          display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: 0,
                        }}
                      >
                        <span style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
                          {category.label}
                        </span>
                      </button>

                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: 13, color: '#6b7280' }}>
                          {preferences[category.id] ? 'On' : 'Off'}
                        </span>
                        <button
                          aria-pressed={preferences[category.id]}
                          onClick={() => handleToggle(category.id)}
                          className={`dpdp-toggle${preferences[category.id] ? ' is-on' : ''}`}
                          style={{ width: 46, height: 26, borderRadius: 16 }}
                        >
                          <div className="dpdp-toggle-knob" style={{ left: preferences[category.id] ? 23 : 3, top: 3 }} />
                        </button>
                      </div>
                    </div>

                    {expandedSections[category.id] && (
                      <div style={{ padding: 12 }}>
                        <p style={{ color: '#374151', margin: 0 }}>
                          {category.description}
                        </p>
                        {category.detailsUrl ? (
                          <a href={category.detailsUrl} style={{ color: primaryBg, display: 'inline-block', marginTop: 8, textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">More Details</a>
                        ) : (
                          <button type="button" onClick={() => toggleSection(category.id)} style={{ background: 'transparent', border: 'none', color: primaryBg, padding: 0, marginTop: 8, cursor: 'pointer' }}>Hide Details</button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Acknowledgement checkboxes */}
              <div className="dpdp-checkbox-group" style={{ margin: '1.25rem 0 0' }}>
                <label className="dpdp-checkbox-label">
                  <input
                    type="checkbox"
                    checked={checked.notice}
                    onChange={() => setChecked(s => ({ ...s, notice: !s.notice }))}
                  />
                  <span>
                    I have read and understood the{' '}
                    <a href={config.privacyUrl} target="_blank" rel="noopener noreferrer">
                      Privacy Notice
                    </a>.
                  </span>
                </label>
                <label className="dpdp-checkbox-label">
                  <input
                    type="checkbox"
                    checked={checked.consent}
                    onChange={() => setChecked(s => ({ ...s, consent: !s.consent }))}
                  />
                  <span>I consent to the collection and processing of my personal data for the purposes mentioned above.</span>
                </label>
              </div>

              <div className="dpdp-modal-actions" style={{ justifyContent: 'flex-end', gap: '8px' }}>
                <button
                  className="dpdp-modal-btn dpdp-modal-btn-outline"
                  style={{ flex: '0 1 auto' }}
                  onClick={() => setShowPreferences(false)}
                >
                  Back
                </button>
                <button
                  className="dpdp-modal-btn dpdp-modal-btn-outline"
                  style={{ flex: '0 1 auto' }}
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button
                  className="dpdp-modal-btn dpdp-modal-btn-primary"
                  style={{ flex: '0 1 auto', opacity: canProceed ? 1 : 0.5, cursor: canProceed ? 'pointer' : 'not-allowed' }}
                  onClick={() => canProceed && handleConfirm(preferences)}
                  disabled={!canProceed}
                >
                  Confirm My Choices
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="dpdp-modal-footer">
          <strong style={{ color: '#111827' }}>{config.appName} {config.complianceLabel}</strong>
        </div>
      </div>
    </div>
  );
}